import classNames from "classnames"

import type {
  ComponentPropsWithoutRef,
  FunctionComponentElement,
  Ref,
} from "react"

import {
  Children,
  forwardRef,
  useMemo,
} from "react"

import styles from "./IconButtonLabel.module.scss"
import { useCloneElement } from "src/hooks"

enum StylePreset {
  withBackground = "iconButtonLabelWithBackground",
  withoutBackground = "iconButtonLabelWithoutBackground",
}

enum ColorPreset {
  default = "iconButtonLabelDefaultColor",
  inverted = "iconButtonLabelInvertedColor",
}

type StylePresetKey = keyof typeof StylePreset
export const defaultStylePreset: StylePresetKey = "withBackground"

type ColorPresetKey = keyof typeof ColorPreset
export const defaultColorPreset: ColorPresetKey = "default"

type LabelAttributes = ComponentPropsWithoutRef<"label">

export interface Props extends LabelAttributes {
  children?: FunctionComponentElement<SVGElement>;
  stylePreset?: StylePresetKey;
  colorPreset?: ColorPresetKey;
}

/*
 * the IconButtonLabel looks just like an IconButton but it is a label
 */
export const IconButtonLabel = forwardRef<HTMLLabelElement, Props>(
  function IconButtonLabel(props: Props, propRef: Ref<HTMLLabelElement>) {
    const {
      children,
      stylePreset = defaultStylePreset,
      colorPreset = defaultColorPreset,
      ...htmlLabelProps
    } = props

    const child = Children.only<FunctionComponentElement<SVGElement>>(children || <svg />)

    const svgProps = useMemo(
      () => ({
        className: styles.iconButtonIcon,
      }),
      [],
    )

    const clonedChild = useCloneElement<SVGElement>(
      child,
      svgProps,
    )

    const className = classNames(
      htmlLabelProps.className,
      styles.iconButtonLabel,
      styles[ColorPreset[colorPreset]],
      styles[StylePreset[stylePreset]],
    )

    return (
      <label
        {...htmlLabelProps}
        ref={propRef}
        className={className}
      >
        {clonedChild}
      </label>
    )
  },
)
